import ArrowBack from "@mui/icons-material/ArrowBack";
import { Box, type SxProps, type Theme, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SystemTabs } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab, SystemTabBadge } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import { ConflictBlockedDialog, type ConflictGroup, type ConflictItem } from "../../../components/UIToolkit/Popups/ConflictBlockedDialog";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useAllJobs } from "../../../hooks/useJobs";
import { FONT_SIZE, LAYOUT_OFFSET, SHADOW } from "../../../theme/tokens";
import type { HrisIntegrationType } from "../../../utils/models";
import { AirtableImportWizard } from "./forms/airtable/AirtableImportWizard";
import { type DepartmentMetadataTab, GeneralDepartmentMetadata } from "./forms/GeneralDepartmentMetadata";
import { useDeptConfig } from "./forms/hooks";
import { useJobNotEnabledEmployees } from "./forms/hrisMapping/hrisMappingHooks";
import { HrisIntegrationSummaryDialog } from "./forms/hrisSummary/HrisIntegrationSummaryDialog";
import type { GeneralFormData, SubmitDeptConfigResult } from "./forms/types";
import { conflictToggleLabel, openShiftItem, recurringShiftItem, scheduledShiftItem, templateLocationItem } from "./jobRemovalConflictLabels";
import { conflictJobLabel, nameableConflictJobNames, nameableJobNameById } from "./jobRemovalConflicts";

const TAB_PARAM = "tab";
const VALID_TABS: DepartmentMetadataTab[] = ["general", "rates", "shifts", "staff-app", "integrations", "hris-mapping", "feature-flags", "locations"];
const isValidTab = (v: string | null): v is DepartmentMetadataTab => v !== null && (VALID_TABS as string[]).includes(v);

const Card = styled("div")({
	padding: "20px 30px",
	boxShadow: "0px 4px 8px 0px #00000008",
});
const BackButton = styled(Box)({
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	color: "var(--inhouse-primary-dark)",
	cursor: "pointer",
	"&:hover": {
		opacity: 0.7,
	},
});

const cardPadding = 1;
const cardSx = { p: cardPadding } as const satisfies SxProps<Theme>;

export const DepartmentConfiguration: FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { track } = useAnalytics();
	const { userInfo } = useAuth();
	const isSupport = !!userInfo?.support;
	const [openHrisSummary, setOpenHrisSummary] = useState<HrisIntegrationType | null>(null);
	const openHrisIntegrationSummary = useCallback(
		(summaryType: HrisIntegrationType, openSource: "manual" | "integration_saved", departmentId: string) => {
			track("hris_integration_summary_opened", {
				summary_type: summaryType,
				department_id: departmentId,
				open_source: openSource,
			});
			setOpenHrisSummary(summaryType);
		},
		[track],
	);
	const handleHrisIntegrationSaved = useCallback(
		(integrationType: HrisIntegrationType, savedDepartmentId: string) => openHrisIntegrationSummary(integrationType, "integration_saved", savedDepartmentId),
		[openHrisIntegrationSummary],
	);
	const handleOpenHrisIntegrationSummary = useCallback(
		(summaryType: HrisIntegrationType) => openHrisIntegrationSummary(summaryType, "manual", chosenDepartment.department_id),
		[chosenDepartment.department_id, openHrisIntegrationSummary],
	);
	const {
		isNewDept,
		setIsNewDept,
		isTimezoneLocked,
		isTimezoneLockUnknown,
		generalFormData,
		setGeneralFormData,
		syncGeneralFormData,
		submitDeptConfig,
		isUpdatingDeptConfig,
		saveDisabledReasons,
		isDirty,
		tabErrorCounts,
		jobRemovalConflicts,
		dismissJobRemovalConflicts,
		pendingNursaWebhookFacilityId,
		registerNursaWebhookNow,
		confirmNursaWebhookSubscription,
		dismissNursaWebhookPrompt,
		isRegisteringNursaWebhook,
		existingDepartmentLocations,
		setExistingDepartmentLocations,
		newDepartmentLocations,
		setNewDepartmentLocations,
		setModifiedDepartmentLocations,
	} = useDeptConfig({ onHrisIntegrationSaved: handleHrisIntegrationSaved });
	const { data: allJobs = [] } = useAllJobs();
	// DAT-464: a save that de-enables a job still in use is refused server-side. Translate the 409's
	// payload into the conflict dialog's shape. The job name goes in the TITLE, not on every group, so
	// the categories read as "Active employees" / "Scheduled shifts" instead of repeating "Aide —" five
	// times. Names ride along behind a disclosure — the count is what matters at 188.
	const jobNameById = useMemo(() => nameableJobNameById(allJobs), [allJobs]);

	/**
	 * Only the jobs this dialog can actually NAME. The `unknown` sentinel is excluded because the rest
	 * of the UI hides it, and an id missing from the catalog is excluded because the alternative was
	 * printing a raw UUID — both produced a refusal that named something the reader had never seen
	 * ("Unknown is still in use"). When nothing is nameable the title falls back to a generic phrasing
	 * rather than inventing a label; the conflict itself is still listed in full below it.
	 */
	const jobConflictNames = useMemo(
		() => (jobRemovalConflicts ? nameableConflictJobNames(Object.keys(jobRemovalConflicts), allJobs) : []),
		[jobRemovalConflicts, allJobs],
	);

	const jobConflictTitle = jobConflictNames.length
		? t("department.job.remove.conflict.title", { jobNames: jobConflictNames.join(", ") })
		: t("department.job.remove.conflict.title.unnamed", { count: jobRemovalConflicts ? Object.keys(jobRemovalConflicts).length : 0 });

	const jobConflictGroups = useMemo<ConflictGroup[]>(() => {
		if (!jobRemovalConflicts) return [];
		// One job is the common case, so groups are unprefixed. With several, prefix to disambiguate —
		// by the same nameable-only rule as the title, so neither can print a raw UUID or the hidden
		// sentinel. A job the dialog cannot name still gets a prefix, or two jobs read as one listed twice.
		const several = Object.keys(jobRemovalConflicts).length > 1;
		const prefix = (jobId: string, label: string) =>
			several ? `${conflictJobLabel(jobId, jobNameById, t("department.config.shifts.job.unknown"))} — ${label}` : label;

		// DAT-805: every category names the rows behind its count, not just the employee one. `items` is a
		// server-capped sample, so the toggle label says "first N of M" whenever it is short of `count`.
		// `titleKey` is the tile's title-case label; `nounKey` is the lower-case plural the toggle
		// sentence needs. Both are passed literally rather than derived, so a key stays greppable.
		const itemised = <T,>(
			titleKey: string,
			nounKey: string,
			jobId: string,
			count: number,
			rows: T[] | undefined,
			toItem: (row: T) => ConflictItem,
		): ConflictGroup => ({
			label: prefix(jobId, t(titleKey)),
			count,
			// DEFAULT: an absent item array degrades to no list rather than throwing. The four `…Items`
			// fields are new in DAT-805, so a frontend deployed ahead of the backend must fall back to
			// DAT-464's counts-only dialog — still a correct refusal, just less specific.
			items: (rows ?? []).map(toItem),
			itemsToggleLabel: conflictToggleLabel(rows?.length ?? 0, count, nounKey, t),
			itemsHideLabel: t("department.job.remove.conflict.hideItems", { category: t(nounKey) }),
		});

		return Object.entries(jobRemovalConflicts).flatMap(([jobId, detail]) => [
			{
				label: prefix(jobId, t("department.job.remove.conflict.employees")),
				count: detail.activeEmployees.length,
				items: detail.activeEmployees.map((employee) => ({
					id: employee.nurseId,
					label: employee.name,
					tag: employee.hasOtherJobs ? t("department.job.remove.conflict.hasOtherJobs") : t("department.job.remove.conflict.onlyJob"),
					tagTone: employee.hasOtherJobs ? ("neutral" as const) : ("warning" as const),
				})),
				itemsToggleLabel: t("department.job.remove.conflict.showEmployees", { count: detail.activeEmployees.length }),
				itemsHideLabel: t("department.job.remove.conflict.hideEmployees"),
			},
			itemised(
				"department.job.remove.conflict.scheduledShifts",
				"department.job.remove.conflict.noun.scheduledShifts",
				jobId,
				detail.scheduledShifts,
				detail.scheduledShiftItems,
				(shift) => scheduledShiftItem(shift, t),
			),
			itemised(
				"department.job.remove.conflict.recurringShifts",
				"department.job.remove.conflict.noun.recurringShifts",
				jobId,
				detail.recurringShifts,
				detail.recurringShiftItems,
				(shift) => recurringShiftItem(shift, t),
			),
			itemised(
				"department.job.remove.conflict.templateLocations",
				"department.job.remove.conflict.noun.templateLocations",
				jobId,
				detail.templateLocations,
				detail.templateLocationItems,
				(template) => templateLocationItem(template, t),
			),
			itemised(
				"department.job.remove.conflict.openShifts",
				"department.job.remove.conflict.noun.openShifts",
				jobId,
				detail.openShifts,
				detail.openShiftItems,
				(request) => openShiftItem(request, t),
			),
		]);
	}, [jobRemovalConflicts, jobNameById, t]);
	const [airtableWizardOpen, setAirtableWizardOpen] = useState(false);
	/**
	 * Set when the Airtable wizard has handed back a merged form that still has to be saved.
	 *
	 * The merge and the submit MUST NOT happen in the same tick. `submitDeptConfig` is a `useCallback`
	 * over `generalFormData`, and `useUpdateGeneralForm(generalFormData)` closes over that value when the
	 * hook is CALLED — so `setGeneralFormData(merged); submitDeptConfig();` sends the form as it was
	 * before the merge, silently dropping every accepted row while reporting success. A ref rather than
	 * state because this is a one-shot instruction to the next render, not something anything renders.
	 *
	 * It holds the RESOLVER rather than a flag so the wizard can await the outcome: `submitDeptConfig`
	 * short-circuits silently when the merged form trips a validator (a rule list whose roles are the
	 * canonical "all" marker is the live case), and without an answer the wizard would close on a save
	 * that never happened.
	 */
	const pendingAirtableApply = useRef<((result: SubmitDeptConfigResult) => void) | null>(null);

	const handleAirtableApply = useCallback(
		(merged: GeneralFormData) =>
			new Promise<SubmitDeptConfigResult>((resolve) => {
				pendingAirtableApply.current = resolve;
				// The dirtying setter on purpose: the merge is a user edit, and the save below clears it.
				setGeneralFormData(merged);
			}),
		[setGeneralFormData],
	);

	// Fires on the render AFTER the merge landed, where `submitDeptConfig` closes over the merged form.
	// `generalFormData` is redundant only because `submitDeptConfig` currently lists it; it is declared
	// anyway because it is the value this effect actually waits for. If that callback were ever memoized
	// more tightly, dropping the dep would stop the effect re-firing and the apply would silently save
	// the pre-merge form again — the exact bug this whole deferral exists to prevent.
	// biome-ignore lint/correctness/useExhaustiveDependencies: see above — the extra dep is deliberate.
	useEffect(() => {
		const resolve = pendingAirtableApply.current;
		if (!resolve) return;
		pendingAirtableApply.current = null;
		void (async () => {
			try {
				resolve(await submitDeptConfig());
			} catch {
				// `submitDeptConfig` handles its own errors; this only guards an unexpected throw from
				// leaving the wizard awaiting a promise that never settles.
				resolve({ saved: false });
			}
		})();
	}, [generalFormData, submitDeptConfig]);

	const [newDeptConfirmOpen, setNewDeptConfirmOpen] = useState(false);
	// Guard the explicit Back exit ourselves: a router-level `useBlocker` can't be used (the app already
	// registers the one blocker the router allows); `beforeunload` below covers reload/close.
	const [pendingBack, setPendingBack] = useState(false);

	useEffect(() => {
		if (!isDirty) return;
		const handler = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [isDirty]);
	// Persist active tab in `?tab=`. Refresh + share-link both land on the same tab.
	const [searchParams, setSearchParams] = useSearchParams();
	const fromUrl = searchParams.get(TAB_PARAM);
	const currentTab: DepartmentMetadataTab = isValidTab(fromUrl) ? fromUrl : "general";
	const setCurrentTab = useCallback(
		(next: DepartmentMetadataTab | ((prev: DepartmentMetadataTab) => DepartmentMetadataTab)) => {
			const resolved = typeof next === "function" ? next(currentTab) : next;
			const params = new URLSearchParams(searchParams);
			params.set(TAB_PARAM, resolved);
			setSearchParams(params, { replace: true });
		},
		[currentTab, searchParams, setSearchParams],
	);
	/** The HRIS mapping tab is auto-saved when users make changes, so the Save and Create New Department
	 * actions in the header are irrelevant there and we hide them to avoid confusion.
	 * */
	const showDeptActions = currentTab !== "hris-mapping" && currentTab !== "feature-flags";

	// Error dot points the user at the tab to fix when the offending fields are on a tab they aren't viewing.
	const errorDot = (value: DepartmentMetadataTab): SystemTabBadge | undefined =>
		(tabErrorCounts[value] ?? 0) > 0 ? { dot: true, color: "error", ariaLabel: t("department.config.tab.hasErrors") } : undefined;

	// DAT-834: how many active employees hold a job this department doesn't enable. A WARNING COUNT, not
	// the red `errorDot` above — that one means "Save is blocked by an invalid field", and this is
	// neither an error nor form state. It rides the Jobs tab because that is where enabling the job is,
	// and it reads from the query directly rather than `tabErrorCounts`, which is validation-only.
	const { data: jobNotEnabledEmployees } = useJobNotEnabledEmployees(chosenDepartment.department_id, !isNewDept);
	const jobNotEnabledCount = jobNotEnabledEmployees?.length ?? 0;
	const jobsTabBadge: SystemTabBadge | undefined =
		jobNotEnabledCount > 0
			? { content: jobNotEnabledCount, color: "warning", ariaLabel: t("department.config.jobs.notEnabled.title", { count: jobNotEnabledCount }) }
			: undefined;

	// HRIS mapping only exists for saved departments. Feature flags read a support-gated endpoint, so
	// the tab is offered only to the users who can actually see anything in it — and only for a saved
	// department, since flags are evaluated against a real department id.
	const tabs: SystemTab<DepartmentMetadataTab>[] = [
		{ label: t("department.config.tab.general"), value: "general", badge: errorDot("general") },
		{ label: t("department.config.tab.rates"), value: "rates", badge: errorDot("rates") },
		{ label: t("department.config.tab.shifts"), value: "shifts", badge: jobsTabBadge },
		{ label: t("department.config.tab.staffApp"), value: "staff-app" },
		{ label: t("department.config.tab.integrations"), value: "integrations", badge: errorDot("integrations") },
		...(!isNewDept ? [{ label: t("department.config.tab.locations"), value: "locations" as DepartmentMetadataTab }] : []),
		...(isSupport && !isNewDept ? [{ label: t("department.config.tab.featureFlags"), value: "feature-flags" as DepartmentMetadataTab }] : []),
		...(!isNewDept ? [{ label: t("hris.mapping.tab.title"), value: "hris-mapping" as DepartmentMetadataTab }] : []),
	];

	const navigateBack = useCallback(() => navigate(`/${chosenDepartment.department_id}/settings`), [navigate, chosenDepartment.department_id]);
	const handleBackClick = () => {
		if (isDirty && !isUpdatingDeptConfig) {
			setPendingBack(true);
			return;
		}
		navigateBack();
	};
	const handleOpenHrisMapping = useCallback(() => {
		setOpenHrisSummary(null);
		const params = new URLSearchParams(searchParams);
		params.set(TAB_PARAM, "hris-mapping");
		setSearchParams(params, { replace: true });
	}, [searchParams, setSearchParams]);

	return (
		<Box
			data-testid="dept-config-root"
			component="form"
			onSubmit={submitDeptConfig}
			sx={{
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
				gap: "15px",
				color: "primary.main",
			}}
		>
			<Box
				component="header"
				sx={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					boxShadow: SHADOW.HEADER_BRAND,
				}}
			>
				<Box
					sx={{
						minHeight: LAYOUT_OFFSET.PAGE_HEADER_MIN_HEIGHT,
						width: "100%",
						px: 1,
						pt: 1,
						display: "flex",
						justifyContent: "start",
						alignItems: "center",
						gap: "20px",
					}}
				>
					<BackButton onClick={handleBackClick} data-testid="dept-config-back">
						<ArrowBack sx={{ width: "20px", height: "15px", m: 0, p: 0 }} />
						<Box component="span" sx={{ fontWeight: 500 }}>
							{t("back")}
						</Box>
					</BackButton>
					<Box
						component="h1"
						sx={{
							p: 0,
							m: 0,
							fontSize: "26px",
							color: "primary.main",
							flex: "1 1 100%",
						}}
					>
						{t("department.configuration")}
					</Box>
					{showDeptActions && (
						<>
							{isNewDept ? (
								<SystemButton onClick={() => setIsNewDept(false)} buttonVariant="secondary" data-testid="dept-config-cancel-new">
									{t("cancel")}
								</SystemButton>
							) : (
								<SystemButton onClick={() => (isDirty ? setNewDeptConfirmOpen(true) : setIsNewDept(true))} data-testid="dept-config-create-new">
									{t("department.config.createNew")}
								</SystemButton>
							)}
							{/* Support-only, and only for a SAVED department: the diff is computed against a
							    persisted config, and a department that does not exist yet has nothing to diff. Same
							    gate shape as the Feature Flags tab above. */}
							{isSupport && !isNewDept && (
								<SystemButton buttonVariant="secondary" onClick={() => setAirtableWizardOpen(true)} data-testid="dept-config-airtable-import">
									{t("department.config.airtable.open")}
								</SystemButton>
							)}
							<Tooltip
								title={
									<>
										{t("department.config.save.disabled.tooltip.title")}
										<Box component="ul" sx={{ m: "4px 0 0", pl: 2.5 }}>
											{saveDisabledReasons.map((reason) => (
												<li key={reason}>{t(reason)}</li>
											))}
										</Box>
									</>
								}
								disableHoverListener={saveDisabledReasons.length === 0}
								disableFocusListener={saveDisabledReasons.length === 0}
								disableTouchListener={saveDisabledReasons.length === 0}
							>
								<span data-testid="dept-config-save-tooltip">
									{/* `loading` (not just `disabled`): the save now runs a server-side conflict check across employees,
											shifts, recurrings, template locations and open shifts, so on a large department there is a
											visible pause before either success or the blocking dialog. A greyed-out button alone reads as
											"nothing happened". */}
									<SystemButton
										data-testid={"dept-config-save"}
										type={"submit"}
										loading={isUpdatingDeptConfig}
										disabled={saveDisabledReasons.length > 0 || isUpdatingDeptConfig}
									>
										{t("save")}
									</SystemButton>
								</span>
							</Tooltip>
						</>
					)}
				</Box>
				<Box sx={{ px: 1 }}>
					<SystemTabs tabs={tabs} currentTab={currentTab} onTabChange={setCurrentTab} dataTestId="dept-config-tabs" />
				</Box>
				{isNewDept && (
					<Box
						data-testid="dept-config-new-banner"
						sx={{ px: 2, py: 0.75, bgcolor: "warning.light", color: "warning.dark", fontSize: FONT_SIZE.SECONDARY, fontWeight: 600 }}
					>
						{t("department.config.newDept.banner")}
					</Box>
				)}
			</Box>
			<Box sx={{ width: "100%", flex: 1, minHeight: 0, overflowY: "auto" }}>
				<Card sx={cardSx}>
					<GeneralDepartmentMetadata
						formData={generalFormData}
						setFormData={setGeneralFormData}
						syncFormData={syncGeneralFormData}
						isNewDept={isNewDept}
						isTimezoneLocked={isTimezoneLocked}
						isTimezoneLockUnknown={isTimezoneLockUnknown}
						currentTab={currentTab}
						isDirty={isDirty}
						onRegisterNursaWebhook={registerNursaWebhookNow}
						isRegisteringNursaWebhook={isRegisteringNursaWebhook}
						onOpenHrisSummary={handleOpenHrisIntegrationSummary}
						existingDepartmentLocations={existingDepartmentLocations}
						setExistingDepartmentLocations={setExistingDepartmentLocations}
						newDepartmentLocations={newDepartmentLocations}
						setNewDepartmentLocations={setNewDepartmentLocations}
						setModifiedDepartmentLocations={setModifiedDepartmentLocations}
						isUpdatingDepartmentLocations={isUpdatingDeptConfig}
					/>
				</Card>
			</Box>

			<ConflictBlockedDialog
				open={jobRemovalConflicts !== null}
				onClose={dismissJobRemovalConflicts}
				title={jobConflictTitle}
				description={t("department.job.remove.conflict.description")}
				groups={jobConflictGroups}
				dismissLabel={t("common.gotIt")}
				dataTestId="job-removal-conflict-dialog"
			/>
			<SystemDialog
				open={pendingBack}
				onClose={() => setPendingBack(false)}
				title={t("department.config.unsaved.title")}
				body={t("department.config.unsaved.body")}
				confirm={t("department.config.unsaved.discard")}
				cancel={t("department.config.unsaved.keep")}
				onConfirm={() => {
					setPendingBack(false);
					navigateBack();
				}}
				dataTestId="dept-config-unsaved-dialog"
			/>
			<SystemDialog
				open={newDeptConfirmOpen}
				onClose={() => setNewDeptConfirmOpen(false)}
				title={t("department.config.newDept.confirm.title")}
				body={t("department.config.newDept.confirm.body")}
				confirm={t("department.config.unsaved.discard")}
				cancel={t("cancel")}
				onConfirm={() => {
					setNewDeptConfirmOpen(false);
					setIsNewDept(true);
				}}
				dataTestId="dept-config-new-confirm-dialog"
			/>
			{/* Saving a NEW Nursa Facility ID maps the department but does not make Nursa talk to us —
			    it only sends shift events for facilities on our webhook registration. Offered here
			    rather than done automatically because the registration is shared by every Nursa
			    facility we integrate with. Declining leaves the saved id untouched. */}
			<SystemDialog
				open={pendingNursaWebhookFacilityId !== null}
				onClose={dismissNursaWebhookPrompt}
				title={t("department.config.nursa.webhook.confirm.title")}
				body={t("department.config.nursa.webhook.confirm.body", { facilityId: pendingNursaWebhookFacilityId })}
				confirm={t("department.config.nursa.webhook.confirm.confirm")}
				cancel={t("department.config.nursa.webhook.confirm.cancel")}
				confirmLoading={isRegisteringNursaWebhook}
				onConfirm={confirmNursaWebhookSubscription}
				dataTestId="dept-config-nursa-webhook-dialog"
			/>
			{airtableWizardOpen && (
				<AirtableImportWizard
					open
					onClose={() => setAirtableWizardOpen(false)}
					formData={generalFormData}
					isDirty={isDirty}
					onApply={handleAirtableApply}
					// Recomputed from the MERGED form once the apply lands, so a refused save can name the
					// fields that blocked it instead of just failing silently.
					saveBlockedReasons={saveDisabledReasons.map((reason) => t(reason))}
				/>
			)}
			{openHrisSummary && (
				<HrisIntegrationSummaryDialog
					key={openHrisSummary}
					open
					summaryType={openHrisSummary}
					onClose={() => setOpenHrisSummary(null)}
					onOpenMapping={handleOpenHrisMapping}
				/>
			)}
		</Box>
	);
};
